import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Download, Loader2, Edit, Building2, ChevronLeft, ChevronRight, Phone, Mail, MapPin, CreditCard } from 'lucide-react';
import BrandsSelector from '../components/BrandsSelector';
import CompaniesSelector from '../components/CompaniesSelector';
import { useModulePermissions } from '@/hooks/useModulePermissions';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + '/api';
const PAGE_SIZE = 20;

function SuppliersEnhanced() {
  const modulePerms = useModulePermissions();
  const canCreateSupplier = modulePerms.canCreate('suppliers');
  const canEditSupplier = modulePerms.canEdit('suppliers');

  const suppliersState = useState([]);
  const suppliers = suppliersState[0];
  const setSuppliers = suppliersState[1];

  const loadingState = useState(true);
  const loading = loadingState[0];
  const setLoading = loadingState[1];

  const searchState = useState('');
  const searchTerm = searchState[0];
  const setSearchTerm = searchState[1];

  const dialogState = useState(false);
  const dialogOpen = dialogState[0];
  const setDialogOpen = dialogState[1];

  const editDialogState = useState(false);
  const editDialogOpen = editDialogState[0];
  const setEditDialogOpen = editDialogState[1];

  const editingState = useState(null);
  const editingSupplier = editingState[0];
  const setEditingSupplier = editingState[1];

  const exportingState = useState(false);
  const exporting = exportingState[0];
  const setExporting = exportingState[1];

  const selectedState = useState([]);
  const selectedSuppliers = selectedState[0];
  const setSelectedSuppliers = selectedState[1];

  const roleState = useState('user');
  const userRole = roleState[0];
  const setUserRole = roleState[1];

  const pageState = useState(1);
  const currentPage = pageState[0];
  const setCurrentPage = pageState[1];

  const savingState = useState(false);
  const saving = savingState[0];
  const setSaving = savingState[1];

  const formState = useState({
    name: '', contact_person: '', email: '', phone: '', address: '',
    gstin: '', credit_days: 30, fssai_number: '', bank_account_number: '', bank_ifsc: '', bank_name: '', brands_handled: [], sub_brands_handled: [], companies_handled: []
  });
  const formData = formState[0];
  const setFormData = formState[1];

  const gstErrorState = useState('');
  const gstError = gstErrorState[0];
  const setGstError = gstErrorState[1];

  const fssaiErrorState = useState('');
  const fssaiError = fssaiErrorState[0];
  const setFssaiError = fssaiErrorState[1];

  const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}Z[A-Z0-9]{1}$/;
  const FSSAI_REGEX = /^[0-9]{14}$/;

  useEffect(function() {
    fetchSuppliers();
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || 'user');
      }
    } catch (e) {
      console.warn('Failed to decode auth token for role', e);
    }
  }, []);

  function fetchSuppliers() {
    const token = localStorage.getItem('token');
    axios.get(API + '/suppliers', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      setSuppliers(response.data);
      setLoading(false);
    }).catch(function() {
      toast.error('Failed to fetch suppliers');
      setLoading(false);
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!validateGstAndFssai()) return;
    setSaving(true);
    const token = localStorage.getItem('token');
    const payload = Object.assign({}, formData, { credit_days: formData.credit_days === '' ? null : formData.credit_days });

    axios.post(API + '/suppliers', payload, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Supplier created successfully');
      setDialogOpen(false);
      resetForm();
      fetchSuppliers();
      setSaving(false);
    }).catch(function(error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
      setSaving(false);
    });
  }

  function handleEditSubmit(e) {
    e.preventDefault();
    if (!editingSupplier) return;
    if (!validateGstAndFssai()) return;
    setSaving(true);
    const token = localStorage.getItem('token');
    const payload = Object.assign({}, formData, { documents: editingSupplier.documents || [], credit_days: formData.credit_days === '' ? null : formData.credit_days });

    axios.put(API + '/suppliers/' + editingSupplier.id, payload, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Supplier updated successfully');
      setEditDialogOpen(false);
      setEditingSupplier(null);
      resetForm();
      fetchSuppliers();
      setSaving(false);
    }).catch(function(error) {
      toast.error(error.response?.data?.detail || 'Update failed');
      setSaving(false);
    });
  }

  function openEditDialog(supplier) {
    setEditingSupplier(supplier);
    setGstError('');
    setFssaiError('');
    setFormData({
      name: supplier.name || '',
      contact_person: supplier.contact_person || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || '',
      gstin: supplier.gstin || '',
      credit_days: supplier.credit_days ?? 30,
      fssai_number: supplier.fssai_number || '',
      bank_account_number: supplier.bank_account_number || '',
      bank_ifsc: supplier.bank_ifsc || '',
      bank_name: supplier.bank_name || '',
      brands_handled: supplier.brands_handled || [],
      sub_brands_handled: supplier.sub_brands_handled || [],
      companies_handled: supplier.companies_handled || []
    });
    setEditDialogOpen(true);
  }

  function resetForm() {
    setFormData({
      name: '', contact_person: '', email: '', phone: '', address: '',
      gstin: '', credit_days: 30, fssai_number: '', bank_account_number: '', bank_ifsc: '', bank_name: '', brands_handled: [], sub_brands_handled: [], companies_handled: []
    });
    setGstError('');
    setFssaiError('');
    setSaving(false);
  }

  function validateGstAndFssai() {
    const valid = true;
    const gst = (formData.gstin || '').trim();
    if (gst && !GST_REGEX.test(gst)) {
      setGstError('Invalid GST Number format. Must be 15 chars: 2 digits + 5 letters + 4 digits + 1 letter + 1 alphanumeric + Z + 1 alphanumeric');
      valid = false;
    } else {
      setGstError('');
    }
    const fssai = (formData.fssai_number || '').trim();
    if (fssai && !FSSAI_REGEX.test(fssai)) {
      setFssaiError('Invalid FSSAI Number. Must be exactly 14 digits');
      valid = false;
    } else {
      setFssaiError('');
    }
    return valid;
  }

  function toggleBrand(brandName) {
    setFormData(function(prev) {
      const current = prev.brands_handled || [];
      const updated = current.includes(brandName)
        ? current.filter(function(b) { return b !== brandName; })
        : current.concat([brandName]);
      return Object.assign({}, prev, { brands_handled: updated });
    });
  }

  function toggleSubBrand(brandName) {
    setFormData(function(prev) {
      const current = prev.sub_brands_handled || [];
      const updated = current.includes(brandName)
        ? current.filter(function(b) { return b !== brandName; })
        : current.concat([brandName]);
      return Object.assign({}, prev, { sub_brands_handled: updated });
    });
  }

  function toggleCompany(companyName) {
    setFormData(function(prev) {
      const current = prev.companies_handled || [];
      const updated = current.includes(companyName)
        ? current.filter(function(c) { return c !== companyName; })
        : current.concat([companyName]);
      return Object.assign({}, prev, { companies_handled: updated });
    });
  }

  function updateField(field, value) {
    setFormData(function(prev) {
      const updated = {};
      updated[field] = value;
      return Object.assign({}, prev, updated);
    });
  }

  // Search filter: name, phone, GSTIN (case insensitive)
  const filteredSuppliers = suppliers.filter(function(s) {
    const q = searchTerm.toLowerCase();
    if (!q) return true;
    return (s.name || '').toLowerCase().includes(q) ||
      (s.phone || '').toLowerCase().includes(q) ||
      (s.gstin || '').toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q) ||
      (s.contact_person || '').toLowerCase().includes(q);
  });

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredSuppliers.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedSuppliers = filteredSuppliers.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Reset page when search changes
  useEffect(function() { setCurrentPage(1); }, [searchTerm]);

  const canExport = ['admin', 'manager'].includes(userRole);

  function handleExport(exportSelected) {
    setExporting(true);
    const token = localStorage.getItem('token');
    const payload = {};
    if (exportSelected && selectedSuppliers.length > 0) {
      payload.supplier_ids = selectedSuppliers;
    } else if (searchTerm) {
      payload.search = searchTerm;
    }
    axios.post(API + '/export/suppliers', payload, {
      headers: { Authorization: 'Bearer ' + token },
      responseType: 'blob'
    }).then(function(response) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Suppliers_Export_' + new Date().toISOString().slice(0,10) + '.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exported suppliers');
      setExporting(false);
    }).catch(function(error) {
      toast.error(error.response?.data?.detail || 'Failed to export');
      setExporting(false);
    });
  }

  function toggleSupplierSelection(supplierId) {
    setSelectedSuppliers(function(prev) {
      return prev.includes(supplierId)
        ? prev.filter(function(id) { return id !== supplierId; })
        : prev.concat([supplierId]);
    });
  }

  if (loading) {
    return React.createElement('div', { className: 'flex items-center justify-center h-full py-20' },
      React.createElement(Loader2, { className: 'w-6 h-6 animate-spin text-slate-400 mr-2' }),
      'Loading suppliers...'
    );
  }

  // Vertical structured form with sections
  function renderFormFields() {
    return React.createElement('div', { className: 'space-y-6' },
      // Section 1: Basic Info
      React.createElement('div', { className: 'space-y-4' },
        React.createElement('div', { className: 'flex items-center gap-2 pb-2 border-b border-slate-200' },
          React.createElement(Building2, { size: 16, className: 'text-blue-600' }),
          React.createElement('h3', { className: 'font-semibold text-sm text-slate-700 uppercase tracking-wide' }, 'Basic Information')
        ),
        React.createElement('div', { className: 'space-y-3' },
          React.createElement('div', { className: 'space-y-1' },
            React.createElement(Label, { htmlFor: 'name' }, 'Company Name *'),
            React.createElement(Input, {
              id: 'name', value: formData.name, required: true, placeholder: 'Enter company name',
              onChange: function(e) { updateField('name', e.target.value); },
              'data-testid': 'supplier-name-input'
            })
          ),
          React.createElement('div', { className: 'space-y-1' },
            React.createElement(Label, { htmlFor: 'contact_person' }, 'Contact Person *'),
            React.createElement(Input, {
              id: 'contact_person', value: formData.contact_person, required: true, placeholder: 'Enter contact person name',
              onChange: function(e) { updateField('contact_person', e.target.value); },
              'data-testid': 'supplier-contact-input'
            })
          ),
          React.createElement('div', { className: 'grid grid-cols-2 gap-3' },
            React.createElement('div', { className: 'space-y-1' },
              React.createElement(Label, { htmlFor: 'email' }, 'Email *'),
              React.createElement(Input, {
                id: 'email', type: 'email', value: formData.email, required: true, placeholder: 'email@example.com',
                onChange: function(e) { updateField('email', e.target.value); },
                'data-testid': 'supplier-email-input'
              })
            ),
            React.createElement('div', { className: 'space-y-1' },
              React.createElement(Label, { htmlFor: 'phone' }, 'Phone *'),
              React.createElement(Input, {
                id: 'phone', value: formData.phone, required: true, placeholder: 'Enter phone number',
                onChange: function(e) { updateField('phone', e.target.value); },
                'data-testid': 'supplier-phone-input'
              })
            )
          )
        )
      ),

      // Section 2: Business Info
      React.createElement('div', { className: 'space-y-4' },
        React.createElement('div', { className: 'flex items-center gap-2 pb-2 border-b border-slate-200' },
          React.createElement(CreditCard, { size: 16, className: 'text-green-600' }),
          React.createElement('h3', { className: 'font-semibold text-sm text-slate-700 uppercase tracking-wide' }, 'Business Details')
        ),
        React.createElement('div', { className: 'space-y-3' },
          React.createElement('div', { className: 'space-y-1' },
            React.createElement(Label, { htmlFor: 'credit_days' }, 'Credit Period (days)'),
            React.createElement(Input, {
              id: 'credit_days', type: 'number', min: 0, max: 365, value: formData.credit_days,
              onChange: function(e) { setFormData(Object.assign({}, formData, { credit_days: e.target.value === '' ? '' : parseInt(e.target.value, 10) })); },
              'data-testid': 'supplier-credit-days-input'
            }),
            React.createElement('p', { className: 'text-xs text-slate-500' }, 'Used for invoice due dates in Vendor Payment Summary (default 30)')
          ),
          React.createElement('div', { className: 'space-y-1' },
            React.createElement(Label, { htmlFor: 'gstin' }, 'GSTIN'),
            React.createElement(Input, {
              id: 'gstin', value: formData.gstin, placeholder: 'e.g., 29ABCDE1234F1Z5',
              onChange: function(e) {
                const val = e.target.value.toUpperCase().replace(/\s/g, '');
                updateField('gstin', val);
                if (val && !GST_REGEX.test(val)) {
                  setGstError('Invalid GST Number format');
                } else {
                  setGstError('');
                }
              },
              maxLength: 15,
              className: 'font-mono' + (gstError ? ' border-red-400 focus-visible:ring-red-400' : ''),
              'data-testid': 'supplier-gstin-input'
            }),
            gstError && React.createElement('p', { className: 'text-xs text-red-500', 'data-testid': 'gstin-error' }, gstError)
          ),
          React.createElement('div', { className: 'space-y-1' },
            React.createElement(Label, { htmlFor: 'fssai_number' }, 'FSSAI License Number'),
            React.createElement(Input, {
              id: 'fssai_number', value: formData.fssai_number || '', placeholder: 'e.g., 10016011000123',
              onChange: function(e) {
                const val = e.target.value.replace(/\D/g, '').slice(0, 14);
                updateField('fssai_number', val);
                if (val && !FSSAI_REGEX.test(val)) {
                  setFssaiError('Invalid FSSAI Number. Must be exactly 14 digits');
                } else {
                  setFssaiError('');
                }
              },
              maxLength: 14,
              className: 'font-mono' + (fssaiError ? ' border-red-400 focus-visible:ring-red-400' : ''),
              'data-testid': 'supplier-fssai-input'
            }),
            fssaiError && React.createElement('p', { className: 'text-xs text-red-500', 'data-testid': 'fssai-error' }, fssaiError)
          )
        )
      ),

      // Section 3: Address
      React.createElement('div', { className: 'space-y-4' },
        React.createElement('div', { className: 'flex items-center gap-2 pb-2 border-b border-slate-200' },
          React.createElement(MapPin, { size: 16, className: 'text-orange-600' }),
          React.createElement('h3', { className: 'font-semibold text-sm text-slate-700 uppercase tracking-wide' }, 'Address Details')
        ),
        React.createElement('div', { className: 'space-y-1' },
          React.createElement(Label, { htmlFor: 'address' }, 'Full Address *'),
          React.createElement('textarea', {
            id: 'address', value: formData.address, required: true, rows: 3,
            placeholder: 'Enter full address with city, state, pincode',
            onChange: function(e) { updateField('address', e.target.value); },
            className: 'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'data-testid': 'supplier-address-input'
          })
        )
      ),

      // Section 4: Bank Details
      React.createElement('div', { className: 'space-y-4' },
        React.createElement('div', { className: 'flex items-center gap-2 pb-2 border-b border-slate-200' },
          React.createElement(CreditCard, { size: 16, className: 'text-purple-600' }),
          React.createElement('h3', { className: 'font-semibold text-sm text-slate-700 uppercase tracking-wide' }, 'Bank Details')
        ),
        React.createElement('div', { className: 'space-y-3' },
          React.createElement('div', { className: 'space-y-1' },
            React.createElement(Label, { htmlFor: 'bank_name' }, 'Bank Name'),
            React.createElement(Input, {
              id: 'bank_name', value: formData.bank_name, placeholder: 'Enter bank name',
              onChange: function(e) { updateField('bank_name', e.target.value); },
              'data-testid': 'supplier-bank-name-input'
            })
          ),
          React.createElement('div', { className: 'grid grid-cols-2 gap-3' },
            React.createElement('div', { className: 'space-y-1' },
              React.createElement(Label, { htmlFor: 'bank_account_number' }, 'Account Number'),
              React.createElement(Input, {
                id: 'bank_account_number', value: formData.bank_account_number, placeholder: 'Enter account number',
                onChange: function(e) { updateField('bank_account_number', e.target.value); },
                'data-testid': 'supplier-account-input'
              })
            ),
            React.createElement('div', { className: 'space-y-1' },
              React.createElement(Label, { htmlFor: 'bank_ifsc' }, 'IFSC Code'),
              React.createElement(Input, {
                id: 'bank_ifsc', value: formData.bank_ifsc, placeholder: 'e.g., KKBK0008141',
                onChange: function(e) { updateField('bank_ifsc', e.target.value.toUpperCase()); },
                className: 'font-mono',
                'data-testid': 'supplier-ifsc-input'
              })
            )
          )
        )
      )
    );
  }

  // Supplier table row
  function renderSupplierRow(supplier) {
    return React.createElement('tr', {
      key: supplier.id,
      className: 'border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ' +
        (selectedSuppliers.includes(supplier.id) ? 'bg-blue-50' : ''),
      onClick: function() {
        if (canEditSupplier) { openEditDialog(supplier); }
      },
      'data-testid': 'supplier-row-' + supplier.id
    },
      canExport && React.createElement('td', { className: 'p-3 w-10' },
        React.createElement('input', {
          type: 'checkbox',
          checked: selectedSuppliers.includes(supplier.id),
          onChange: function(e) { e.stopPropagation(); toggleSupplierSelection(supplier.id); },
          onClick: function(e) { e.stopPropagation(); },
          className: 'rounded border-slate-300'
        })
      ),
      React.createElement('td', { className: 'p-3' },
        React.createElement('div', null,
          React.createElement('p', { className: 'font-semibold text-slate-800' }, supplier.name),
          React.createElement('p', { className: 'text-xs text-slate-500' }, supplier.contact_person)
        )
      ),
      React.createElement('td', { className: 'p-3' },
        React.createElement('div', { className: 'space-y-0.5' },
          React.createElement('p', { className: 'text-sm flex items-center gap-1' },
            React.createElement(Phone, { size: 12, className: 'text-slate-400' }), supplier.phone
          ),
          React.createElement('p', { className: 'text-sm flex items-center gap-1 text-slate-500' },
            React.createElement(Mail, { size: 12, className: 'text-slate-400' }), supplier.email
          )
        )
      ),
      React.createElement('td', { className: 'p-3' },
        supplier.gstin
          ? React.createElement('span', { className: 'font-mono text-xs bg-slate-100 px-2 py-1 rounded' }, supplier.gstin)
          : React.createElement('span', { className: 'text-xs text-slate-400' }, '-')
      ),
      React.createElement('td', { className: 'p-3' },
        supplier.fssai_number
          ? React.createElement('span', { className: 'font-mono text-xs bg-green-50 px-2 py-1 rounded text-green-700' }, supplier.fssai_number)
          : React.createElement('span', { className: 'text-xs text-slate-400' }, '-')
      ),
      React.createElement('td', { className: 'p-3' },
        supplier.brands_handled && supplier.brands_handled.length > 0
          ? React.createElement('div', { className: 'flex flex-wrap gap-1' },
              supplier.brands_handled.slice(0, 3).map(function(b, i) {
                return React.createElement(Badge, { key: i, variant: 'outline', className: 'text-xs bg-blue-50 border-blue-200 text-blue-700' }, b);
              }),
              supplier.brands_handled.length > 3 && React.createElement(Badge, {
                variant: 'outline', className: 'text-xs'
              }, '+' + (supplier.brands_handled.length - 3))
            )
          : React.createElement('span', { className: 'text-xs text-slate-400' }, '-')
      ),
      React.createElement('td', { className: 'p-3' },
        supplier.companies_handled && supplier.companies_handled.length > 0
          ? React.createElement('div', { className: 'flex flex-wrap gap-1' },
              supplier.companies_handled.slice(0, 2).map(function(c, i) {
                return React.createElement(Badge, { key: i, variant: 'outline', className: 'text-xs bg-purple-50 border-purple-200 text-purple-700' }, c);
              }),
              supplier.companies_handled.length > 2 && React.createElement(Badge, {
                variant: 'outline', className: 'text-xs'
              }, '+' + (supplier.companies_handled.length - 2))
            )
          : React.createElement('span', { className: 'text-xs text-slate-400' }, '-')
      ),
      React.createElement('td', { className: 'p-3 text-right' },
        canEditSupplier && React.createElement(Button, {
          variant: 'ghost', size: 'sm',
          onClick: function(e) { e.stopPropagation(); openEditDialog(supplier); },
          'data-testid': 'edit-supplier-' + supplier.id
        },
          React.createElement(Edit, { size: 14, className: 'text-slate-500' })
        )
      )
    );
  }

  return React.createElement('div', { className: 'space-y-4', 'data-testid': 'supplier-management' },
    // Header
    React.createElement('div', { className: 'flex items-center justify-between' },
      React.createElement('div', null,
        React.createElement('h1', { className: 'text-2xl font-bold text-slate-800' }, 'Supplier Management'),
        React.createElement('p', { className: 'text-sm text-slate-500 mt-0.5' },
          filteredSuppliers.length + ' supplier' + (filteredSuppliers.length !== 1 ? 's' : '') + ' found'
        )
      ),
      React.createElement('div', { className: 'flex items-center gap-2' },
        canExport && selectedSuppliers.length > 0 && React.createElement(Button, {
          variant: 'outline', size: 'sm',
          onClick: function() { handleExport(true); },
          disabled: exporting, className: 'gap-1'
        }, React.createElement(Download, { size: 14 }), 'Export (' + selectedSuppliers.length + ')'),
        canExport && React.createElement(Button, {
          variant: 'outline', size: 'sm',
          onClick: function() { handleExport(false); },
          disabled: exporting, className: 'gap-1'
        }, exporting ? React.createElement(Loader2, { size: 14, className: 'animate-spin' }) : React.createElement(Download, { size: 14 }), 'Export All'),
        canExport && React.createElement(CsvDownloadButton, { onDownload: function() { handleExport(selectedSuppliers.length > 0); }, testId: 'suppliers-export-csv', disabled: exporting }),

        // Add Supplier
        canCreateSupplier && React.createElement(Dialog, { open: dialogOpen, onOpenChange: setDialogOpen },
          React.createElement(DialogTrigger, { asChild: true },
            React.createElement(Button, { className: 'gap-1', 'data-testid': 'add-supplier-btn' },
              React.createElement(Plus, { size: 16 }), 'Add Supplier'
            )
          ),
          React.createElement(DialogContent, { className: 'max-w-xl max-h-[90vh] overflow-y-auto' },
            React.createElement(DialogHeader, null,
              React.createElement(DialogTitle, null, 'Add New Supplier')
            ),
            React.createElement('form', { onSubmit: handleSubmit, className: 'space-y-4' },
              renderFormFields(),
              React.createElement(BrandsSelector, {
                selectedParentBrands: formData.brands_handled,
                selectedSubBrands: formData.sub_brands_handled,
                onToggleParentBrand: toggleBrand,
                onToggleSubBrand: toggleSubBrand
              }),
              React.createElement(CompaniesSelector, {
                selectedCompanies: formData.companies_handled,
                onToggleCompany: toggleCompany
              }),
              React.createElement('div', { className: 'flex justify-end gap-2 border-t pt-4' },
                React.createElement(Button, { type: 'button', variant: 'outline', onClick: function() { setDialogOpen(false); } }, 'Cancel'),
                React.createElement(Button, { type: 'submit', disabled: saving, 'data-testid': 'submit-supplier-btn' },
                  saving ? React.createElement(Loader2, { size: 14, className: 'animate-spin mr-1' }) : null,
                  'Create Supplier'
                )
              )
            )
          )
        )
      )
    ),

    // Search bar
    React.createElement('div', { className: 'relative' },
      React.createElement(Search, { className: 'absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400', size: 16 }),
      React.createElement(Input, {
        placeholder: 'Search by name, phone, GSTIN, email...',
        value: searchTerm,
        onChange: function(e) { setSearchTerm(e.target.value); },
        className: 'pl-9 h-10 bg-white',
        'data-testid': 'supplier-search-input'
      })
    ),

    // Table
    React.createElement(Card, { className: 'border-slate-200 overflow-hidden' },
      React.createElement('div', { className: 'overflow-x-auto' },
        React.createElement('table', { className: 'w-full' },
          React.createElement('thead', null,
            React.createElement('tr', { className: 'bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500' },
              canExport && React.createElement('th', { className: 'p-3 w-10' }),
              React.createElement('th', { className: 'p-3 text-left font-semibold' }, 'Supplier'),
              React.createElement('th', { className: 'p-3 text-left font-semibold' }, 'Contact'),
              React.createElement('th', { className: 'p-3 text-left font-semibold' }, 'GSTIN'),
              React.createElement('th', { className: 'p-3 text-left font-semibold' }, 'FSSAI'),
              React.createElement('th', { className: 'p-3 text-left font-semibold' }, 'Brands'),
              React.createElement('th', { className: 'p-3 text-left font-semibold' }, 'Companies'),
              React.createElement('th', { className: 'p-3 w-16' })
            )
          ),
          React.createElement('tbody', null,
            paginatedSuppliers.length > 0
              ? paginatedSuppliers.map(renderSupplierRow)
              : React.createElement('tr', null,
                  React.createElement('td', { colSpan: 7, className: 'p-8 text-center text-slate-400' },
                    searchTerm ? 'No suppliers match "' + searchTerm + '"' : 'No suppliers found. Add your first supplier.'
                  )
                )
          )
        )
      ),

      // Pagination
      filteredSuppliers.length > PAGE_SIZE && React.createElement('div', {
        className: 'flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50'
      },
        React.createElement('span', { className: 'text-sm text-slate-500' },
          'Showing ' + ((safePage - 1) * PAGE_SIZE + 1) + '-' + Math.min(safePage * PAGE_SIZE, filteredSuppliers.length) + ' of ' + filteredSuppliers.length
        ),
        React.createElement('div', { className: 'flex items-center gap-1' },
          React.createElement(Button, {
            variant: 'outline', size: 'sm', disabled: safePage <= 1,
            onClick: function() { setCurrentPage(safePage - 1); },
            'data-testid': 'prev-page-btn'
          }, React.createElement(ChevronLeft, { size: 14 })),
          React.createElement('span', { className: 'px-3 text-sm font-medium' }, safePage + ' / ' + totalPages),
          React.createElement(Button, {
            variant: 'outline', size: 'sm', disabled: safePage >= totalPages,
            onClick: function() { setCurrentPage(safePage + 1); },
            'data-testid': 'next-page-btn'
          }, React.createElement(ChevronRight, { size: 14 }))
        )
      )
    ),

    // Edit Supplier Dialog
    React.createElement(Dialog, {
      open: editDialogOpen,
      onOpenChange: function(open) {
        setEditDialogOpen(open);
        if (!open) { setEditingSupplier(null); resetForm(); }
      }
    },
      React.createElement(DialogContent, { className: 'max-w-xl max-h-[90vh] overflow-y-auto' },
        React.createElement(DialogHeader, null,
          React.createElement(DialogTitle, { 'data-testid': 'edit-dialog-title' }, 'Edit Supplier: ' + (editingSupplier?.name || ''))
        ),
        React.createElement('form', { onSubmit: handleEditSubmit, className: 'space-y-4' },
          renderFormFields(),
          React.createElement(BrandsSelector, {
            selectedParentBrands: formData.brands_handled,
            selectedSubBrands: formData.sub_brands_handled,
            onToggleParentBrand: toggleBrand,
            onToggleSubBrand: toggleSubBrand
          }),
          React.createElement(CompaniesSelector, {
            selectedCompanies: formData.companies_handled,
            onToggleCompany: toggleCompany
          }),
          React.createElement('div', { className: 'flex justify-end gap-2 border-t pt-4' },
            React.createElement(Button, { type: 'button', variant: 'outline', onClick: function() { setEditDialogOpen(false); } }, 'Cancel'),
            React.createElement(Button, { type: 'submit', disabled: saving, 'data-testid': 'update-supplier-btn' },
              saving ? React.createElement(Loader2, { size: 14, className: 'animate-spin mr-1' }) : null,
              'Update Supplier'
            )
          )
        )
      )
    )
  );
}

export default SuppliersEnhanced;
